import {
	Box,
	Chip,
	Collapse,
	Divider,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { type FC, useRef } from "react";
import type { PortfolioListReportingSummaryResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioListItemRedesign } from "web_myplann/Portfolio/Components/Redesign/PortfolioListItemRedesign";
import { portfolioGroupLabel } from "web_myplann/Portfolio/Helpers/portfolioCategoryMapping";
import type { PortfolioCategory } from "web_myplann/Portfolio/Types/portfolioCategory";
import { useScrolledPastTop } from "web_myplann/Shared/Hooks/useScrolledPastTop";

type Props = {
	category: PortfolioCategory;
	collapsed: boolean;
	onToggle: () => void;
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined;
};

export const PortfolioCategorySection: FC<Props> = ({
	category,
	collapsed,
	onToggle,
	summaryData,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const sentinelRef = useRef<HTMLDivElement>(null);
	const stuck = useScrolledPastTop(sentinelRef);

	return (
		<Box position="relative">
			<Box
				ref={sentinelRef}
				aria-hidden
				position="absolute"
				top={0}
				height="1px"
				width="100%"
			/>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				onClick={onToggle}
				px={theme.spacingTokens.lg}
				pt={stuck ? theme.spacingTokens.sm : 2.4}
				pb={theme.spacingTokens.sm}
				bgcolor={theme.palette.grey[50]}
				boxShadow={stuck ? theme.shadow.elevated : "none"}
				zIndex={2}
				top={0}
				position="sticky"
				sx={{
					cursor: "pointer",
					transition: theme.transitions.create(["padding-top", "box-shadow"], {
						duration: theme.transitions.duration.shorter,
					}),
				}}
			>
				<Stack direction="row" alignItems="center" gap={theme.spacingTokens.sm}>
					<Typography variant="overline" color={theme.palette.grey[500]}>
						{t(portfolioGroupLabel(category.id))}
					</Typography>
					<Chip
						variant="tag"
						label={category.items.length}
						sx={{ color: theme.palette.grey[400] }}
					/>
				</Stack>
				{collapsed ? (
					<ChevronDownIcon
						size={theme.iconSize.sm}
						color={theme.palette.grey[400]}
					/>
				) : (
					<ChevronUpIcon
						size={theme.iconSize.sm}
						color={theme.palette.grey[400]}
					/>
				)}
			</Stack>

			<Collapse in={!collapsed} timeout="auto" unmountOnExit>
				<Box bgcolor="background.paper">
					{category.items.map((item, index) => {
						const isLast = index === category.items.length - 1;
						return (
							<Box key={`${item.kind}-${item.data.id}`}>
								<PortfolioListItemRedesign
									item={item}
									summaryData={summaryData}
								/>
								{!isLast && <Divider redesigned />}
							</Box>
						);
					})}
				</Box>
			</Collapse>
		</Box>
	);
};
